import React from 'react';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useSellerQueries from '@dashboard/hooks/queries/useSellerQueries';
import Card from '@dashboard/components/Card';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import CardContent from '@dashboard/components/CardContent';
import { Form } from '@dashboard/components/Formik';
import * as Yup from 'yup';
import SettingsDescription from '@dashboard/components/SettingsDescription';
import SettingsContainer from '@/dashboard/components/SettingsContainer';

const validationSchema = Yup.object().shape({
  businessProfile: Yup.object().shape({
    businessName: Yup.string(),
    businessWebsite: Yup.string().url('Must be a valid URL'),
    address: Yup.object().shape({
      streetAddress: Yup.string(),
      streetAddress2: Yup.string(),
      city: Yup.string(),
      state: Yup.string(),
      postalCode: Yup.string(),
      country: Yup.string(),
    }),
    taxId: Yup.string(),
    privacyPolicyUrl: Yup.string().url('Must be a valid URL'),
    termsOfServiceUrl: Yup.string().url('Must be a valid URL'),
  }),
});

const SettingsPublicDetails = () => {
  const { seller } = useAuth();
  const { updateSeller } = useSellerQueries();

  return (
    <Card variant="root">
      <CardHeader variant="root">
        <div className="flex flex-col max-w-lg">
          <CardHeaderTitle variant="root">Public details</CardHeaderTitle>
          <SettingsDescription className="mt-2">
            Manage customer facing contact details. These details are displayed in your store,
            emails, invoices and receipts.
          </SettingsDescription>
        </div>
      </CardHeader>

      <CardContent variant="root" className="mt-4 mb-32">
        <SettingsContainer size="lg">
          <Form
            enableReinitialize
            validationSchema={validationSchema}
            initialValues={{
              url: seller.url,
              supportEmail: seller.supportEmail,
              supportWebsite: seller.supportWebsite,
              businessProfile: {
                businessName: seller.businessProfile?.businessName,
                address: {
                  streetAddress: seller.businessProfile?.address?.streetAddress || '',
                  streetAddress2: seller.businessProfile?.address?.streetAddress2 || '',
                  city: seller.businessProfile?.address?.city || '',
                  state: seller.businessProfile?.address?.state || '',
                  postalCode: seller.businessProfile?.address?.postalCode || '',
                  country: seller.businessProfile?.address?.country || '',
                },
                taxId: seller.businessProfile?.taxId || '',
                privacyPolicyUrl: seller.businessProfile?.privacyPolicyUrl || '',
                termsOfServiceUrl: seller.businessProfile?.termsOfServiceUrl || '',
              },
            }}
            fields={[
              {
                name: 'url',
                type: 'url',
                label: 'Website URL',
                labelDescription: 'Link your logo & store name to your website or social profile.',
              },
              {
                name: 'supportEmail',
                type: 'email',
                label: 'Support email',
                labelDescription: 'Email address where customers can contact you for support.',
              },
              {
                name: 'supportWebsite',
                type: 'url',
                label: 'Support website',
                labelDescription: 'Website where customers can contact you for support.',
              },
              {
                name: 'businessProfile.businessName',
                type: 'text',
                label: 'Business name',
              },
              {
                name: 'businessProfile.address.streetAddress',
                type: 'text',
                label: 'Street address',
              },
              {
                name: 'businessProfile.address.streetAddress2',
                type: 'text',
                label: 'Street address 2',
              },
              {
                name: 'businessProfile.address.city',
                type: 'text',
                label: 'City',
              },
              {
                name: 'businessProfile.address.state',
                type: 'text',
                label: 'State',
              },
              {
                name: 'businessProfile.address.postalCode',
                type: 'text',
                label: 'Postal code',
              },
              {
                name: 'businessProfile.address.country',
                type: 'text',
                label: 'Country',
              },
              {
                name: 'businessProfile.taxId',
                type: 'text',
                label: 'Tax ID',
              },
              {
                name: 'businessProfile.privacyPolicyUrl',
                type: 'url',
                label: 'Privacy policy URL',
              },
              {
                name: 'businessProfile.termsOfServiceUrl',
                type: 'url',
                label: 'Terms of service URL',
              },
            ]}
            onSubmit={async values => {
              try {
                const { data } = await updateSeller(values);

                return { data };
              } catch (error) {
                console.log(error);
                return { error };
              }
            }}
          />
        </SettingsContainer>
      </CardContent>
    </Card>
  );
};

export default SettingsPublicDetails;
